import { addDays, startOfWeek } from 'date-fns'
import {
  CalendarIcon,
  CalendarRangeIcon,
  ChartLineIcon,
  MessageCircleIcon,
} from 'lucide-react'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { currentWeekStart } from '../data/homework-data'
import { useHomework } from '../providers/homework-provider'
import { CoachingFeedback, useCoachingFeedback } from './coaching-feedback'
import { MonthCalendar } from './month-calendar'
import { StatsView } from './stats-view'
import { WeekStrip } from './week-strip'

function ShowCompletedToggle() {
  const { showCompleted, setShowCompleted } = useHomework()

  return (
    <div className="flex items-center gap-2">
      <Switch
        id="show-completed"
        checked={showCompleted}
        onCheckedChange={setShowCompleted}
        aria-label="Show completed homework tasks"
      />
      <Label htmlFor="show-completed" className="text-sm font-normal">
        Show completed
      </Label>
    </div>
  )
}

type HomeworkMode = 'week' | 'month' | 'feedback'

function ModeToggle({
  mode,
  onChange,
  unreadCount,
}: {
  mode: HomeworkMode
  onChange: (mode: HomeworkMode) => void
  unreadCount: number
}) {
  const options: {
    value: HomeworkMode
    label: string
    icon: typeof CalendarIcon
  }[] = [
    { value: 'week', label: 'Week', icon: CalendarRangeIcon },
    { value: 'month', label: 'Month', icon: CalendarIcon },
    { value: 'feedback', label: 'Feedback', icon: MessageCircleIcon },
  ]

  return (
    <div
      role="radiogroup"
      aria-label="Homework view"
      className="inline-flex items-center rounded-md border p-0.5"
    >
      {options.map((opt) => {
        const active = opt.value === mode
        const Icon = opt.icon
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex items-center gap-1.5 rounded-[calc(var(--radius)-6px)] px-2.5 py-1 text-sm font-medium transition-colors',
              active
                ? 'bg-secondary text-secondary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="size-4" aria-hidden="true" />
            {opt.label}
            {opt.value === 'feedback' && unreadCount > 0 && (
              <Badge className="ml-1 px-1.5 py-0 text-[10px]">
                {unreadCount}
              </Badge>
            )}
          </button>
        )
      })}
    </div>
  )
}

/**
 * You're either doing this week's homework or reviewing feedback on past
 * work — one view at a time, toggled from the same control as Week/Month.
 */
function HomeworkViews({
  weekStart,
  setFocusDay,
  isCurrentWeek,
}: {
  weekStart: Date
  setFocusDay: Dispatch<SetStateAction<Date>>
  isCurrentWeek: boolean
}) {
  const [mode, setMode] = useState<HomeworkMode>('week')
  const { setFocusDate } = useHomework()
  const { unreadCount } = useCoachingFeedback()

  // Keep the queried plan window centered on the viewed week. Re-assert when
  // Week becomes the active mode, not only when `weekStart` changes: MonthCalendar
  // parks `focusDate` on the browsed month while Month is open, so returning to
  // Week must re-sync the window to the current week even though `weekStart`
  // itself never moved.
  useEffect(() => {
    if (mode === 'week') {
      setFocusDate(weekStart)
    }
  }, [mode, weekStart, setFocusDate])

  const handleSelectDay = (day: Date) => {
    setFocusDay(day)
    setMode('week')
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ModeToggle mode={mode} onChange={setMode} unreadCount={unreadCount} />
        {mode !== 'feedback' && <ShowCompletedToggle />}
      </div>

      {mode === 'week' && (
        <WeekStrip
          weekStart={weekStart}
          onNavigate={(dir) => setFocusDay((prev) => addDays(prev, dir * 7))}
          onToday={() => setFocusDay(new Date())}
          isCurrentWeek={isCurrentWeek}
        />
      )}
      {mode === 'month' && <MonthCalendar onSelectDay={handleSelectDay} />}
      {mode === 'feedback' && <CoachingFeedback />}
    </>
  )
}

export function HomeworkSection() {
  // State holds the day the client navigated to, never the week anchor, so the
  // anchor is derived during render.
  const [focusDay, setFocusDay] = useState(() => new Date())
  const weekStart = startOfWeek(focusDay, { weekStartsOn: 1 })
  const isCurrentWeek = weekStart.getTime() === currentWeekStart().getTime()

  return (
    <Tabs defaultValue="homework" className="gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TabsList>
          <TabsTrigger value="homework" className="px-3">
            <CalendarIcon />
            Homework
          </TabsTrigger>
          <TabsTrigger value="progress" className="px-3">
            <ChartLineIcon />
            Progress
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="homework" className="flex flex-col gap-4">
        <HomeworkViews
          weekStart={weekStart}
          setFocusDay={setFocusDay}
          isCurrentWeek={isCurrentWeek}
        />
      </TabsContent>

      <TabsContent value="progress">
        <StatsView />
      </TabsContent>
    </Tabs>
  )
}
